import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { quickSupportQuery, type QuickSupportRow } from '../../api/easeedeskDashboards'
import { Content } from '../../ui/Shell'
import { rangeOf, type RangeName } from './range'
import { NotInstalled, WallHeader } from './WallHeader'

/**
 * "Quick Support Dashboard" - `easeedesk-dashboards/Quick_support::index`,
 * macheta `views/version-3/dashboards/easeedesk/quick_support/index.php` si
 * coloanele din `public/js/dashboards/easeedesk/quick_support.js`.
 *
 * Acelasi tabel: un rand pe tehnician, o coloana pe stare, prima coloana pe
 * gri si restul pe albastrul inchis al machetei (`.left` / `.a-left`).
 * Coloanele vin de la capat, in ordinea din JS-ul vechi.
 *
 * CE NU S-A MUTAT, SI DE CE. Ecranul vechi cerea randurile prin DataTables cu
 * `serverSide: true` si `bPaginate: false` - adica trimitea pagina, cautarea
 * si ordonarea la server, dar cerea de fiecare data TOT. Aici randurile vin o
 * data si se sorteaza in pagina; nu e nicio pagina de pierdut, fiindca nu era
 * niciuna.
 */

const STYLE = `
.od-quick-support table { border-collapse: separate; border-spacing: 2px; font-size: 25px; width: 100%; }
.od-quick-support th, .od-quick-support td { text-align: center; }
.od-quick-support .left { background-color: grey; color: white; }
.od-quick-support .a-left { background-color: #1d1d7a; color: white; }
.od-quick-support th { cursor: pointer; }
`

type SortKey = keyof QuickSupportRow

export function QuickSupportPage() {
  const [rangeName, setRangeName] = useState<RangeName>('Today')
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'new', desc: true })
  const q = useQuery(quickSupportQuery(rangeOf(rangeName)))
  const page = q.data

  const rows = [...(page?.rows ?? [])].sort((a, b) => {
    const left = a[sort.key]
    const right = b[sort.key]
    const cmp = typeof left === 'string' && typeof right === 'string' ? left.localeCompare(right) : Number(left) - Number(right)

    return sort.desc ? -cmp : cmp
  })

  return (
    <Content>
      <style>{STYLE}</style>
      <div className="od-quick-support">
        <WallHeader title={page?.title ?? 'Quick Support Dashboard'} rangeName={rangeName} onRange={setRangeName} />

        {page && !page.ready ? <NotInstalled what="Quick Support" /> : null}

        <table className="table table-hover table-striped" id="quick-details">
          <thead>
            <tr>
              {(page?.columns ?? []).map((column) => (
                <th
                  key={column.key}
                  style={{ width: column.width }}
                  className={column.key === 'name' ? 'left' : 'a-left'}
                  onClick={() =>
                    setSort((was) =>
                      was.key === column.key ? { key: was.key, desc: !was.desc } : { key: column.key as SortKey, desc: true }
                    )
                  }
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} id={`row_${row.id}`}>
                {(page?.columns ?? []).map((column) => (
                  <td key={column.key} className={column.key === 'name' ? 'left' : 'a-left'}>
                    {String(row[column.key as SortKey] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Content>
  )
}
